import { useState } from 'react';

export default function PolicySubsidy() {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedGuide, setSelectedGuide] = useState<string | null>(null);

  const subsidyTypes = [
    {
      id: 'startup-employment',
      title: '创业带动就业补贴',
      description: '初创企业招用员工并稳定就业可获得补贴支持',
      icon: 'ri-team-line',
      amount: '最高3万元',
      period: '一次性补贴',
      conditions: ['初创企业（3年内）', '招用员工签订1年以上合同', '连续缴纳6个月以上社保', '员工仍在企业就业'],
      materials: ['申请表', '营业执照', '劳动合同', '社保缴费明细', '银行开户许可证']
    },
    {
      id: 'flexible-employment',
      title: '灵活就业社保补贴',
      description: '就业困难人员和高校毕业生灵活就业社保费补贴',
      icon: 'ri-shield-user-line',
      amount: '每月800元',
      period: '按月发放',
      conditions: ['就业困难人员或毕业2年内高校毕业生', '灵活就业登记', '个人缴纳社保'],
      materials: ['申请表', '身份证', '就业困难证明或毕业证', '社保缴费凭证', '银行账户信息']
    },
    {
      id: 'startup-grant',
      title: '一次性创业资助',
      description: '符合条件人员创办企业可获得一次性创业资助',
      icon: 'ri-rocket-line',
      amount: '10000元',
      period: '一次性资助',
      conditions: ['在校学生及毕业5年内', '留学回国人员5年内', '军转干部复退军人', '创办企业满6个月', '正常经营缴纳社保'],
      materials: ['申请表', '营业执照', '身份证明', '毕业证书或退役证明', '社保缴费记录', '经营场所证明']
    },
    {
      id: 'training-subsidy',
      title: '职业技能培训补贴',
      description: '参加职业技能培训并取得证书的人员补贴',
      icon: 'ri-graduation-cap-line',
      amount: '500-3500元',
      period: '培训完成后',
      conditions: ['法定劳动年龄内', '参加政府补贴培训目录内项目', '取得职业资格证书或技能等级证书'],
      materials: ['申请表', '身份证', '培训合格证书', '培训机构发票', '银行账户信息']
    },
    {
      id: 'social-insurance',
      title: '社会保险补贴',
      description: '用人单位招用就业困难人员的社保补贴',
      icon: 'ri-shield-check-line',
      amount: '实际缴费额',
      period: '按月补贴',
      conditions: ['招用就业困难人员', '签订1年以上劳动合同', '按规定缴纳社会保险'],
      materials: ['申请表', '营业执照', '劳动合同', '就业困难人员证明', '社保缴费明细']
    },
    {
      id: 'employment-see',
      title: '就业见习补贴',
      description: '吸纳高校毕业生等群体参加就业见习的补贴',
      icon: 'ri-user-follow-line',
      amount: '最低工资标准60%',
      period: '见习期间按月',
      conditions: ['认定为见习基地', '接收毕业2年内高校毕业生', '见习期3-12个月'],
      materials: ['申请表', '见习协议', '毕业证书', '见习人员花名册', '考勤记录']
    }
  ];

  const applicationProcess = [
    {
      step: '1',
      title: '政策了解',
      description: '详细了解各项补贴政策的申请条件和标准',
      icon: 'ri-book-open-line',
      details: ['政策文件研读', '申请条件核查', '补贴标准了解', '申请时限掌握']
    },
    {
      step: '2',
      title: '条件自查',
      description: '对照政策要求进行自我条件评估',
      icon: 'ri-checkbox-multiple-line',
      details: ['企业资格审查', '人员条件核实', '经营状况检查', '社保缴纳确认']
    },
    {
      step: '3',
      title: '材料准备',
      description: '按要求准备完整的申请材料',
      icon: 'ri-file-list-3-line',
      details: ['基础证件收集', '专项材料准备', '电子版制作', '材料真实性确认']
    },
    {
      step: '4',
      title: '在线申报',
      description: '通过政务服务平台提交申请',
      icon: 'ri-upload-cloud-line',
      details: ['平台账号注册', '在线表格填写', '材料上传提交', '申请状态跟踪']
    },
    {
      step: '5',
      title: '审核公示',
      description: '相关部门审核并进行公示',
      icon: 'ri-eye-line',
      details: ['部门审核处理', '现场核查（如需）', '审核结果公示', '异议处理']
    },
    {
      step: '6',
      title: '资金拨付',
      description: '审核通过后拨付补贴资金',
      icon: 'ri-money-dollar-circle-line',
      details: ['拨付清单制作', '财政资金划拨', '企业账户到账', '到账通知确认']
    }
  ];

  const platformGuide = [
    {
      platform: '广东政务服务网',
      url: 'www.gdzwfw.gov.cn',
      icon: 'ri-government-line',
      color: 'bg-blue-500',
      features: ['PC端办理', '功能齐全', '流程完整', '状态跟踪'],
      description: '主要的政务服务平台，支持各类补贴申请'
    },
    {
      platform: '粤省事小程序',
      url: '微信搜索"粤省事"',
      icon: 'ri-smartphone-line',
      color: 'bg-green-500',
      features: ['移动办理', '操作便捷', '随时申请', '消息推送'],
      description: '移动端政务服务，支持部分补贴申请'
    },
    {
      platform: '广州人社局官网',
      url: 'rsj.gz.gov.cn',
      icon: 'ri-user-line',
      color: 'bg-purple-500',
      features: ['专业平台', '就业服务', '政策解读', '咨询服务'],
      description: '人社部门官方网站，就业创业补贴专业平台'
    }
  ];

  const detailedGuides = {
    'startup-employment': {
      title: '创业带动就业补贴详细指南',
      sections: [
        {
          title: '申请条件详解',
          icon: 'ri-checkbox-circle-line',
          content: [
            '【初创企业定义】在广州市行政区域内登记注册3年内的小微企业、个体工商户、民办非企业单位和农民专业合作社、家庭农场等',
            '【招用员工要求】企业需招用员工（不含法定代表人或经营者）并与其签订1年以上期限劳动合同',
            '【社保缴纳要求】为招用员工连续缴纳6个月以上社会保险费，且申请补贴时仍在缴纳',
            '【在岗要求】申请时被招用员工仍在本企业就业，劳动关系存续',
            '【时间限制】企业注册登记之日起3年内可申请，逾期不予受理'
          ]
        },
        {
          title: '补贴标准计算',
          icon: 'ri-calculator-line',
          content: [
            '【基础补贴】招用3人（含3人）以下的，按每人2000元给予补贴',
            '【递增补贴】招用3人以上的，每增加1人给予3000元补贴',
            '【补贴上限】单个企业补贴总额最高不超过3万元',
            '【计算示例1】招用2人：2×2000=4000元',
            '【计算示例2】招用5人：3×2000+2×3000=12000元',
            '【计算示例3】招用15人：3×2000+12×3000=42000元，但最高限额3万元'
          ]
        },
        {
          title: '申请材料清单',
          icon: 'ri-file-list-line',
          content: [
            '【申请表】《广州市创业带动就业补贴申请表》，需加盖企业公章',
            '【营业执照】营业执照副本复印件，加盖公章',
            '【身份证明】法定代表人或负责人身份证复印件',
            '【劳动合同】所有招用员工的劳动合同复印件，体现合同期限',
            '【社保明细】员工社会保险缴费明细表，体现连续缴费记录',
            '【银行证明】企业银行开户许可证或基本存款账户信息',
            '【其他材料】根据具体情况可能需要的补充证明材料'
          ]
        },
        {
          title: '办理流程步骤',
          icon: 'ri-route-line',
          content: [
            '第1步：登录申请 - 访问广东政务服务网（www.gdzwfw.gov.cn），注册并登录企业账号',
            '第2步：搜索服务 - 在搜索框输入"创业带动就业补贴"，选择对应服务事项',
            '第3步：填写申请 - 在线填写《广州市创业带动就业补贴申请表》，确保信息准确',
            '第4步：上传材料 - 按要求上传所有申请材料的电子版，确保清晰可见',
            '第5步：提交申请 - 检查信息无误后提交申请，获得受理编号',
            '第6步：等待审核 - 人社部门在15个工作日内完成审核',
            '第7步：公示阶段 - 审核通过后进行5个工作日公示',
            '第8步：资金拨付 - 公示无异议后，补贴资金拨付到企业银行账户'
          ]
        },
        {
          title: '审核要点说明',
          icon: 'ri-search-eye-line',
          content: [
            '【企业真实性】核查企业是否真实存在，经营地址是否与注册地址一致',
            '【员工真实性】核查招用员工是否真实，是否存在虚假用工情况',
            '【合同有效性】检查劳动合同签订是否规范，期限是否符合要求',
            '【社保连续性】核实社保缴费记录的真实性和连续性',
            '【在岗情况】确认申请时员工是否仍在企业正常工作',
            '【重复申请】检查是否存在重复申请或多重身份申请情况'
          ]
        },
        {
          title: '常见问题解答',
          icon: 'ri-question-line',
          content: [
            'Q: 个体工商户可以申请吗？A: 可以，个体工商户属于初创企业范围',
            'Q: 招用亲属可以申请补贴吗？A: 招用配偶、父母、子女等直系亲属不能申请补贴',
            'Q: 员工离职了还能申请吗？A: 申请时员工必须仍在企业就业，离职员工不能计入',
            'Q: 社保中断一个月影响申请吗？A: 要求连续缴纳6个月以上，中断会影响申请资格',
            'Q: 补贴资金多久能到账？A: 从申请到资金到账一般需要30个工作日左右'
          ]
        }
      ]
    },
    'flexible-employment': {
      title: '灵活就业社保补贴详细指南',
      sections: [
        {
          title: '申请条件说明',
          icon: 'ri-user-settings-line',
          content: [
            '【就业困难人员】包括失业登记满12个月人员、距法定退休年龄不足5年人员、享受最低生活保障人员、持有《中华人民共和国残疾人证》人员等',
            '【高校毕业生】毕业2年内的普通高等学校毕业生，包括全日制大专及以上学历毕业生',
            '【灵活就业登记】需要在公共就业服务机构进行灵活就业登记，并保持登记状态',
            '【个人缴费】以个人身份缴纳职工基本养老保险费和职工基本医疗保险费',
            '【缴费证明】能够提供社会保险费缴费凭证，证明按时足额缴费'
          ]
        },
        {
          title: '补贴标准详情',
          icon: 'ri-money-dollar-box-line',
          content: [
            '【补贴金额】每人每月按800元标准给予补贴',
            '【补贴期限】就业困难人员最长可享受3年，距法定退休年龄不足5年的可延长至退休',
            '【毕业生期限】高校毕业生最长可享受2年',
            '【发放方式】按月发放，直接转入个人银行账户',
            '【补贴范围】仅限于个人缴纳的职工基本养老保险费和职工基本医疗保险费部分'
          ]
        },
        {
          title: '申请材料准备',
          icon: 'ri-file-copy-line',
          content: [
            '【申请表】《灵活就业社保补贴申请表》，本人签字确认',
            '【身份证明】申请人身份证原件及复印件',
            '【资格证明】就业困难人员证明或高校毕业证书复印件',
            '【就业登记】灵活就业登记证明或相关登记材料',
            '【缴费凭证】社会保险费缴费发票或银行缴费凭证',
            '【银行信息】本人银行账户信息，包括开户行和账号'
          ]
        },
        {
          title: '办理流程指引',
          icon: 'ri-guide-line',
          content: [
            '第1步：灵活就业登记 - 先到户籍所在地或常住地公共就业服务机构办理灵活就业登记',
            '第2步：社保缴费 - 以个人身份到社保经办机构缴纳职工基本养老保险和医疗保险',
            '第3步：网上申请 - 通过粤省事小程序或广东政务服务网在线申请',
            '第4步：填写信息 - 填写个人基本信息、就业情况和社保缴费信息',
            '第5步：上传材料 - 上传身份证、资格证明、缴费凭证等材料照片',
            '第6步：提交审核 - 确认信息无误后提交，等待人社部门审核',
            '第7步：按月发放 - 审核通过后，从下月开始按月发放补贴'
          ]
        },
        {
          title: '申请时间要求',
          icon: 'ri-calendar-check-line',
          content: [
            '【申请时限】每月20日前提交申请，当月审核，次月发放补贴',
            '【首次申请】灵活就业后可随时申请，但补贴从申请当月开始计算',
            '【续期申请】每年需要重新申请确认，审核通过后继续享受',
            '【停止条件】不再灵活就业、找到固定工作、停止缴费等情况下停发补贴',
            '【政策衔接】与其他就业创业政策不重复享受'
          ]
        },
        {
          title: '注意事项提醒',
          icon: 'ri-alert-line',
          content: [
            '灵活就业社保补贴与企业职工身份不能同时享受',
            '必须按时足额缴纳社会保险费，欠费期间不予补贴',
            '个人信息发生变化时应及时更新，确保补贴正常发放',
            '虚报、冒领补贴将被追回并纳入诚信记录',
            '补贴期满或不符合条件时应主动停止申请'
          ]
        }
      ]
    },
    'startup-grant': {
      title: '一次性创业资助详细指南',
      sections: [
        {
          title: '申请人员范围',
          icon: 'ri-group-line',
          content: [
            '【在校学生】普通高等学校、职业学校、技工院校在校学生',
            '【应届毕业生】毕业5年内的普通高等学校、职业学校、技工院校毕业生',
            '【留学回国人员】出国（境）留学回国人员，领取学位（学历）证书5年内',
            '【军转人员】军转干部和复退军人',
            '【特定群体】登记失业人员、就业困难人员、返乡创业人员',
            '【创业要求】在广州市行政区域内创办企业、个体工商户、民办非企业单位、农民专业合作社、家庭农场等'
          ]
        },
        {
          title: '创业企业条件',
          icon: 'ri-building-line',
          content: [
            '【注册时间】企业工商登记注册满6个月，且申请时仍在正常经营',
            '【经营状态】企业经营状态正常，无被列入异常名录或严重违法失信名单',
            '【社保缴纳】企业按规定为员工缴纳社会保险费，包括申请人本人',
            '【法人要求】申请人应为企业法定代表人或个体工商户经营者',
            '【唯一性】同一申请人只能享受一次创业资助，不得重复申请'
          ]
        },
        {
          title: '资助标准说明',
          icon: 'ri-gift-line',
          content: [
            '【资助金额】符合条件的创业者一次性给予10000元创业资助',
            '【发放方式】审核通过后一次性拨付到企业银行账户',
            '【资金用途】主要用于创业初期的启动资金支持，无需偿还',
            '【税务处理】按照相关税务规定处理，可能涉及企业所得税',
            '【监督管理】资助资金使用情况接受相关部门监督检查'
          ]
        },
        {
          title: '申请材料详单',
          icon: 'ri-file-text-line',
          content: [
            '【申请表】《一次性创业资助申请表》，申请人签字并加盖企业公章',
            '【营业执照】企业营业执照副本复印件，体现注册时间和经营范围',
            '【身份证明】法定代表人或经营者身份证原件及复印件',
            '【学历证明】毕业证书复印件或学位证书复印件（在校生提供学籍证明）',
            '【特殊身份证明】军转干部提供转业证明，复退军人提供退役证明',
            '【社保证明】企业社会保险缴费记录，证明按规定缴纳社保',
            '【经营场所证明】经营场所租赁合同或房产证明复印件',
            '【银行信息】企业银行开户许可证或基本存款账户信息'
          ]
        },
        {
          title: '办理流程详解',
          icon: 'ri-flow-chart',
          content: [
            '第1步：条件自查 - 对照申请条件进行自我评估，确认符合申请资格',
            '第2步：材料准备 - 按要求收集准备所有申请材料，确保真实完整',
            '第3步：网上申请 - 登录广东政务服务网，搜索"一次性创业资助"服务',
            '第4步：信息填报 - 在线填写申请表格，上传申请材料电子版',
            '第5步：提交审核 - 确认信息准确后提交申请，获得受理回执',
            '第6步：部门审核 - 人社部门在规定时间内完成资格审核和现场核查',
            '第7步：结果公示 - 审核通过的申请进行公示，接受社会监督',
            '第8步：资金拨付 - 公示无异议后，资助资金拨付到企业账户'
          ]
        },
        {
          title: '审核要点关注',
          icon: 'ri-focus-3-line',
          content: [
            '【申请人资格】重点核查申请人是否符合规定的人员范围',
            '【企业真实性】核实企业是否真实经营，是否存在虚假注册',
            '【时间节点】检查企业注册时间是否满6个月，申请是否在有效期内',
            '【社保合规】核查企业社保缴纳情况，是否按规定为员工缴费',
            '【重复申请】通过系统比对防止同一人重复享受资助',
            '【经营状况】现场核查企业实际经营情况和发展前景'
          ]
        },
        {
          title: '办理时限承诺',
          icon: 'ri-time-line',
          content: [
            '【受理时限】材料齐全的当场受理，材料不全的一次性告知补正',
            '【审核时限】自受理之日起45个工作日内完成审核',
            '【公示时限】审核通过后公示5个工作日',
            '【拨付时限】公示结束后10个工作日内完成资金拨付',
            '【总体时限】从申请到资金到账约60个工作日',
            '【加急处理】特殊情况可申请加急处理，缩短办理时间'
          ]
        }
      ]
    },
    'training-subsidy': {
      title: '职业技能培训补贴详细指南',
      sections: [
        {
          title: '补贴对象范围',
          icon: 'ri-group-line',
          content: [
            '【企业职工】企业在职职工参加岗位技能提升培训或转岗转业培训',
            '【就业重点群体】贫困家庭子女、毕业年度高校毕业生、城乡未继续升学初高中毕业生、农村转移就业劳动者、城镇登记失业人员',
            '【失业人员】领取失业保险金期间的失业人员参加职业技能培训',
            '【退役军人】自主就业退役士兵在退役后一年内参加职业技能培训',
            '【残疾人】持有残疾人证的人员参加职业技能培训',
            '【服刑人员】刑满释放人员、社区矫正人员参加职业技能培训'
          ]
        },
        {
          title: '培训项目类别',
          icon: 'ri-graduation-cap-line',
          content: [
            '【技能等级培训】初级工、中级工、高级工、技师、高级技师培训',
            '【专项职业能力培训】针对特定岗位的专项技能培训',
            '【创业培训】SYB创业培训、网络创业培训、创业模拟实训等',
            '【新职业培训】人工智能、大数据、云计算、物联网等新兴职业培训',
            '【安全技能培训】特种作业人员安全技能培训',
            '【家政服务培训】育婴员、养老护理员、家政服务员等培训'
          ]
        },
        {
          title: '补贴标准体系',
          icon: 'ri-money-dollar-circle-line',
          content: [
            '【初级工培训】补贴标准800-1200元/人',
            '【中级工培训】补贴标准1000-1500元/人',
            '【高级工培训】补贴标准1200-2000元/人',
            '【技师培训】补贴标准1500-2500元/人',
            '【高级技师培训】补贴标准2000-3500元/人',
            '【专项能力培训】补贴标准500-800元/人',
            '【创业培训】SYB培训1200元/人，网络创业培训1500元/人',
            '【新职业培训】根据培训成本确定，最高不超过6000元/人'
          ]
        },
        {
          title: '培训机构要求',
          icon: 'ri-building-4-line',
          content: [
            '【资质认定】必须是经人社部门认定的职业技能培训机构',
            '【师资条件】具备相应职业（工种）的专业师资力量',
            '【设施设备】具备满足培训需要的场所、设施设备',
            '【管理制度】建立完善的培训管理、财务管理等制度',
            '【质量保证】建立培训质量监控和评估体系',
            '【诚信记录】近三年内无违法违规行为，信用状况良好'
          ]
        },
        {
          title: '申请条件要求',
          icon: 'ri-checkbox-multiple-line',
          content: [
            '【年龄要求】法定劳动年龄内（16-65周岁）',
            '【培训完成】完成规定课时的培训，出勤率达到80%以上',
            '【证书获得】取得职业资格证书、职业技能等级证书或专项职业能力证书',
            '【就业状况】培训后6个月内实现就业或创业的优先支持',
            '【不重复享受】同一职业（工种）同一等级不重复享受补贴',
            '【时间限制】证书颁发之日起12个月内申请，逾期不予受理'
          ]
        },
        {
          title: '申请材料清单',
          icon: 'ri-file-list-3-line',
          content: [
            '【申请表】《职业技能培训补贴申请表》，本人签字确认',
            '【身份证明】申请人身份证原件及复印件',
            '【证书材料】职业资格证书或职业技能等级证书复印件',
            '【培训证明】培训机构出具的培训合格证书或结业证书',
            '【缴费凭证】培训费用发票或收据原件',
            '【银行账户】本人银行账户信息（开户行、账号）',
            '【就业证明】就业或创业相关证明材料（如适用）',
            '【特殊身份证明】退役军人证、残疾人证等（如适用）'
          ]
        },
        {
          title: '办理流程详解',
          icon: 'ri-route-line',
          content: [
            '第1步：培训报名 - 选择有资质的培训机构，报名参加培训',
            '第2步：完成培训 - 按要求完成培训课程，达到出勤率要求',
            '第3步：考试取证 - 参加职业技能鉴定考试，取得相应证书',
            '第4步：网上申请 - 登录广东政务服务网或粤省事小程序申请',
            '第5步：材料上传 - 上传身份证、证书、发票等材料照片',
            '第6步：提交审核 - 确认信息无误后提交申请',
            '第7步：部门审核 - 人社部门审核申请材料和培训情况',
            '第8步：资金发放 - 审核通过后，补贴资金转入个人银行账户'
          ]
        },
        {
          title: '质量监管机制',
          icon: 'ri-shield-check-line',
          content: [
            '【过程监管】培训过程中进行现场检查，核实培训真实性',
            '【结果评估】对培训效果进行跟踪评估，了解就业情况',
            '【机构考核】对培训机构进行年度考核，实行动态管理',
            '【学员反馈】建立学员满意度调查和投诉处理机制',
            '【资金监管】加强补贴资金使用监管，防止骗取套取',
            '【信用管理】建立培训机构和个人诚信档案'
          ]
        },
        {
          title: '常见问题解答',
          icon: 'ri-question-line',
          content: [
            'Q: 可以同时申请多个工种的培训补贴吗？A: 可以，但同一工种同一等级只能享受一次补贴',
            'Q: 培训期间有生活费补贴吗？A: 就业困难人员和零就业家庭成员培训期间可享受生活费补贴',
            'Q: 网上培训可以申请补贴吗？A: 经认定的线上培训项目可以申请补贴',
            'Q: 企业职工培训补贴如何申请？A: 企业统一组织培训后，由企业代为申请或职工个人申请',
            'Q: 培训不合格可以补考吗？A: 可以补考一次，补考合格后仍可申请补贴'
          ]
        }
      ]
    },
    'social-insurance': {
      title: '社会保险补贴详细指南',
      sections: [
        {
          title: '补贴适用对象',
          icon: 'ri-team-line',
          content: [
            '【招用就业困难人员的用人单位】包括各类企业、个体经济组织、民办非企业单位等',
            '【就业困难人员范围】失业登记满12个月人员、距退休不足5年人员、零就业家庭成员、享受低保人员、持残疾证人员、毕业一年以上未就业高校毕业生等',
            '【公益性岗位人员】在政府开发的公益性岗位就业的人员',
            '【小微企业】招用毕业2年内高校毕业生的小微企业',
            '【社会组织】招用就业困难人员的社会组织和民办非企业单位',
            '【特殊群体】招用退役军人、去产能职工等特殊群体的用人单位'
          ]
        },
        {
          title: '补贴标准计算',
          icon: 'ri-calculator-line',
          content: [
            '【基本标准】按用人单位为就业困难人员实际缴纳的社会保险费给予补贴',
            '【补贴范围】包括基本养老保险、基本医疗保险、失业保险、工伤保险、生育保险',
            '【个人缴费】个人应缴纳的社会保险费由个人承担',
            '【补贴期限】一般最长不超过3年，距退休不足5年的可延长至退休',
            '【毕业生补贴】小微企业招用高校毕业生，补贴期限最长2年',
            '【公益性岗位】公益性岗位补贴期限最长不超过3年'
          ]
        },
        {
          title: '申请条件详解',
          icon: 'ri-file-check-line',
          content: [
            '【劳动关系】与就业困难人员签订1年以上劳动合同',
            '【社保缴费】按规定为招用人员缴纳社会保险费',
            '【工作时间】每周工作时间不少于20小时',
            '【工资标准】支付的工资不低于当地最低工资标准',
            '【合规经营】用人单位依法经营，无违法用工行为',
            '【不重复享受】不与其他就业创业扶持政策重复享受'
          ]
        },
        {
          title: '申请材料准备',
          icon: 'ri-folder-open-line',
          content: [
            '【申请表】《社会保险补贴申请表》，加盖单位公章',
            '【营业执照】用人单位营业执照或其他法人登记证书复印件',
            '【劳动合同】与就业困难人员签订的劳动合同复印件',
            '【身份证明】就业困难人员身份证复印件和就业困难人员认定证明',
            '【社保缴费】社会保险费征缴机构出具的缴费明细账（单）',
            '【工资发放】工资发放明细表或银行代发工资凭证',
            '【银行账户】用人单位银行开户许可证或账户信息',
            '【其他材料】根据具体情况需要的其他证明材料'
          ]
        },
        {
          title: '办理程序流程',
          icon: 'ri-git-branch-line',
          content: [
            '第1步：人员认定 - 确认招用人员属于就业困难人员范围',
            '第2步：签订合同 - 与符合条件人员签订1年以上劳动合同',
            '第3步：社保登记 - 为招用人员办理社会保险参保登记',
            '第4步：正常缴费 - 按规定缴纳社会保险费满3个月后可申请',
            '第5步：网上申报 - 通过广东政务服务网提交补贴申请',
            '第6步：材料审核 - 人社部门审核企业资质和用工情况',
            '第7步：现场核查 - 对企业进行实地核查（如需要）',
            '第8步：资金拨付 - 审核通过后，补贴资金拨付到企业账户'
          ]
        },
        {
          title: '补贴发放管理',
          icon: 'ri-money-cny-circle-line',
          content: [
            '【发放周期】一般按季度申请，按季度发放',
            '【先缴后补】用人单位先行缴费，审核通过后给予补贴',
            '【动态管理】每季度核查用工情况和社保缴费情况',
            '【停发条件】劳动关系终止、停止缴费等情况立即停发',
            '【资金来源】从就业创业专项资金中列支',
            '【监督检查】接受人社、财政、审计等部门监督检查'
          ]
        },
        {
          title: '违规处理规定',
          icon: 'ri-alarm-warning-line',
          content: [
            '【虚假申报】提供虚假材料骗取补贴的，追回资金并处罚款',
            '【挪用资金】将补贴资金挪作他用的，停止拨付并追回资金',
            '【重复申请】重复享受补贴的，追回多领取的资金',
            '【违法用工】存在违法用工行为的，取消补贴资格',
            '【信用记录】违规行为记入企业和个人诚信档案',
            '【法律责任】构成犯罪的，依法追究刑事责任'
          ]
        },
        {
          title: '政策优化措施',
          icon: 'ri-settings-3-line',
          content: [
            '【简化流程】推行网上申报，简化申请材料和审核流程',
            '【提高效率】建立绿色通道，压缩审核时限',
            '【精准服务】加强政策宣传，提供精准指导服务',
            '【动态调整】根据就业形势变化，适时调整补贴政策',
            '【信息共享】加强部门间信息共享，减少重复提交材料',
            '【跟踪评估】定期评估政策实施效果，持续优化完善'
          ]
        }
      ]
    },
    'employment-see': {
      title: '就业见习补贴详细指南',
      sections: [
        {
          title: '见习对象范围',
          icon: 'ri-user-follow-line',
          content: [
            '【高校毕业生】离校2年内未就业的全日制普通高等学校毕业生，包括大专、本科、研究生等各层次毕业生',
            '【中职毕业生】离校2年内未就业的中等职业学校毕业生，包括技工学校毕业生',
            '【失业青年】16-24岁失业青年，在人力资源社会保障部门进行失业登记',
            '【就业困难人员】经认定的就业困难人员中的青年群体',
            '【户籍要求】广州市户籍人员或在广州市求职的非广州市户籍人员',
            '【基本条件】具有完全民事行为能力，身体健康，有就业意愿和见习需求'
          ]
        },
        {
          title: '见习基地认定条件',
          icon: 'ri-building-4-line',
          content: [
            '【单位性质】国家机关、事业单位、社会团体，国有企业、民营企业、外商投资企业等各类用人单位',
            '【经营状况】依法设立，具有法人资格，生产经营正常，管理规范，社会信誉良好',
            '【岗位提供】能够持续提供一定数量适合高校毕业生的见习岗位，见习岗位与毕业生专业相关或适合',
            '【指导能力】有专门人员负责见习指导工作，能够对见习人员进行业务指导和管理',
            '【基础设施】具备见习所需的场所、设备等基本条件，能够保障见习活动正常开展',
            '【安全保障】能够为见习人员提供必要的劳动保护和安全保障措施'
          ]
        },
        {
          title: '补贴标准详解',
          icon: 'ri-money-dollar-box-line',
          content: [
            '【基本补贴标准】按照广州市最低工资标准的60%给予见习补贴，2024年广州市最低工资为2300元/月，见习补贴为1380元/月',
            '【留用奖励标准】见习期满留用率达到50%以上的见习基地，补贴标准提高到最低工资标准的80%，即1840元/月',
            '【人身意外保险】为见习人员购买人身意外伤害保险，保险费用每人每月25元，由政府承担',
            '【补贴期限】见习期限为3-12个月，补贴期限与见习期限一致',
            '【发放方式】补贴资金先拨付给见习基地，再由见习基地按月发放给见习人员',
            '【其他待遇】见习基地可根据见习人员表现给予适当的生活补助或交通补贴'
          ]
        },
        {
          title: '见习管理制度',
          icon: 'ri-settings-4-line',
          content: [
            '【见习协议】见习基地与见习人员应签订见习协议，明确见习期限、见习内容、双方权利义务等',
            '【见习计划】制定详细的见习计划，包括见习岗位、见习内容、指导老师、考核标准等',
            '【过程管理】建立见习考勤制度，记录见习人员的出勤情况和见习表现',
            '【指导机制】为每名见习人员指定专门的指导老师，负责业务指导和日常管理',
            '【考核评价】建立见习考核评价制度，定期对见习人员进行考核评价',
            '【档案管理】建立见习人员档案，记录见习全过程信息和评价结果'
          ]
        },
        {
          title: '申请流程指引',
          icon: 'ri-roadmap-line',
          content: [
            '第1步：基地认定申请 - 符合条件的用人单位向人社部门申请认定为就业见习基地',
            '第2步：岗位信息发布 - 见习基地向人社部门报送见习岗位信息，通过各种渠道发布招募信息',
            '第3步：人员招募对接 - 见习基地与符合条件的见习人员进行对接，双向选择',
            '第4步：签订见习协议 - 见习基地与见习人员签订规范的见习协议，明确权利义务',
            '第5步：见习活动开展 - 按照见习协议和见习计划开展见习活动，做好过程管理',
            '第6步：补贴申请 - 见习基地按季度向人社部门申请见习补贴',
            '第7步：资金拨付 - 人社部门审核后将补贴资金拨付给见习基地',
            '第8步：补贴发放 - 见习基地将补贴按月发放给见习人员'
          ]
        },
        {
          title: '申请材料清单',
          icon: 'ri-file-copy-2-line',
          content: [
            '【基地认定材料】营业执照或法人登记证书、组织机构代码证、法定代表人身份证明',
            '【岗位计划材料】见习岗位设置方案、见习计划、岗位说明书',
            '【管理制度材料】见习管理制度、安全管理制度、指导老师配备情况',
            '【见习人员材料】见习人员身份证、毕业证书、就业失业登记证明',
            '【见习协议】见习基地与见习人员签订的见习协议',
            '【考勤记录】见习期间的考勤记录和见习鉴定表',
            '【保险证明】为见习人员购买人身意外伤害保险的证明材料',
            '【银行信息】见习基地银行开户许可证或基本存款账户信息'
          ]
        },
        {
          title: '质量管理要求',
          icon: 'ri-award-line',
          content: [
            '【动态管理】人社部门对见习基地实行动态管理，定期评估见习基地的见习质量和管理水平',
            '【实地检查】定期组织专家或工作人员到见习基地进行实地检查，了解见习开展情况',
            '【满意度调查】定期开展见习人员满意度调查，收集见习人员对见习基地的评价',
            '【效果跟踪】跟踪见习结束后6个月内见习人员的就业情况和职业发展',
            '【经验交流】组织优秀见习基地开展经验交流和示范推广活动',
            '【奖惩机制】对见习质量高、留用率高的基地给予表彰奖励，对违规基地取消认定资格'
          ]
        },
        {
          title: '政策支持措施',
          icon: 'ri-heart-add-line',
          content: [
            '【岗位开发】根据产业发展需求和毕业生专业特点，积极开发适合的见习岗位',
            '【供需对接】定期举办见习供需对接会，促进见习人员与见习基地的有效对接',
            '【技能提升】鼓励和支持见习基地为见习人员提供相关技能培训和能力提升',
            '【就业服务】为见习人员提供职业指导、就业推荐等配套就业服务',
            '【创业扶持】对有创业意愿的见习人员，提供创业指导、创业培训等创业扶持',
            '【政策宣传】加强政策宣传，提高社会对就业见习政策的知晓度和参与度'
          ]
        },
        {
          title: '注意事项提醒',
          icon: 'ri-error-warning-line',
          content: [
            '见习期间，见习人员与见习基地不建立劳动关系，双方签订的是见习协议而非劳动合同',
            '见习基地应当为见习人员购买人身意外伤害保险，保障见习人员在见习期间的安全',
            '见习人员在见习期间可以参加公务员考试、事业单位招聘等，见习经历可作为基层工作经历',
            '见习基地应当按照见习协议的约定，按时足额向见习人员发放见习补贴',
            '违规使用见习补贴资金或提供虚假材料的，将被取消见习基地资格并追回相关资金'
          ]
        }
      ]
    },
    'employment-apprenticeship': {
      title: '就业见习补贴详细指南',
      sections: [
        {
          title: '见习对象范围',
          icon: 'ri-user-follow-line',
          content: [
            '【高校毕业生】离校2年内未就业的全日制普通高等学校毕业生，包括大专、本科、研究生等各层次毕业生',
            '【中职毕业生】离校2年内未就业的中等职业学校毕业生，包括技工学校毕业生',
            '【失业青年】16-24岁失业青年，在人力资源社会保障部门进行失业登记',
            '【就业困难人员】经认定的就业困难人员中的青年群体',
            '【户籍要求】广州市户籍人员或在广州市求职的非广州市户籍人员',
            '【基本条件】具有完全民事行为能力，身体健康，有就业意愿和见习需求'
          ]
        },
        {
          title: '见习基地条件',
          icon: 'ri-building-4-line',
          content: [
            '【单位性质】国家机关、事业单位、社会团体，国有企业、民营企业、外商投资企业等各类用人单位',
            '【经营状况】依法设立，具有法人资格，生产经营正常，管理规范，社会信誉良好',
            '【岗位提供】能够持续提供一定数量适合高校毕业生的见习岗位，见习岗位与毕业生专业相关或适合',
            '【指导能力】有专门人员负责见习指导工作，能够对见习人员进行业务指导和管理',
            '【基础设施】具备见习所需的场所、设备等基本条件，能够保障见习活动正常开展',
            '【安全保障】能够为见习人员提供必要的劳动保护和安全保障措施'
          ]
        },
        {
          title: '补贴标准',
          icon: 'ri-money-dollar-box-line',
          content: [
            '【基本补贴】按广州市最低工资标准的60%发放见习补贴，2024年广州市最低工资为2300元/月，见习补贴为1380元/月',
            '【留用奖励】见习期满留用率达到50%以上的见习基地，补贴标准提升至80%，即1840元/月',
            '【保险补贴】为见习人员购买人身意外伤害保险，每人每月费用由政府承担',
            '【补贴期限】见习期限为3-12个月，补贴随见习期限发放',
            '【发放方式】补贴资金先拨付给见习基地，再由基地按月发放给见习人员'
          ]
        },
        {
          title: '见习管理',
          icon: 'ri-settings-4-line',
          content: [
            '【协议签订】见习基地与见习人员签订见习协议，明确双方权利义务',
            '【计划制定】制定见习计划，明确岗位、内容、指导老师、考核标准等',
            '【过程管理】建立考勤制度，记录见习人员出勤和表现',
            '【指导机制】为每名见习人员配备专门指导老师',
            '【考核评价】建立考核评价制度，定期评价见习人员',
            '【档案管理】为见习人员建立完整档案，记录全过程信息'
          ]
        },
        {
          title: '申请流程',
          icon: 'ri-roadmap-line',
          content: [
            '第1步：基地认定 - 用人单位向人社部门申请认定为就业见习基地',
            '第2步：岗位发布 - 基地报送见习岗位信息并进行宣传招聘',
            '第3步：人员对接 - 基地与符合条件的见习人员进行匹配对接',
            '第4步：签订协议 - 基地与见习人员签订规范的见习协议',
            '第5步：开展见习 - 按协议和计划组织见习活动，做好管理',
            '第6步：补贴申请 - 基地按季度向人社部门提交补贴申请',
            '第7步：资金拨付 - 人社部门审核后将补贴拨付给基地',
            '第8步：发放补贴 - 基地按月向见习人员发放补贴'
          ]
        },
        {
          title: '所需材料',
          icon: 'ri-file-copy-2-line',
          content: [
            '【认定材料】营业执照、组织机构代码证、法定代表人身份证明等',
            '【岗位材料】见习岗位设置方案、见习计划、岗位说明书',
            '【制度文件】见习管理制度、安全管理制度、指导老师配备情况',
            '【人员材料】见习人员身份证、毕业证、失业登记证明',
            '【协议文件】见习协议',
            '【考勤资料】见习期间考勤记录和鉴定表',
            '【保险证明】人身意外伤害保险购买证明',
            '【银行信息】基地银行开户许可证或基本存款账户信息'
          ]
        },
        {
          title: '质量监督',
          icon: 'ri-award-line',
          content: [
            '【动态监督】人社部门对见习基地实行动态监督，定期评估',
            '【现场检查】组织专家实地检查见习基地运行情况',
            '【满意度调查】开展见习人员满意度调查',
            '【跟踪效果】跟踪毕业见习人员的就业情况',
            '【经验推广】组织优秀基地经验交流和示范推广',
            '【奖惩机制】对表现优秀的基地予以奖励，违规的取消资格'
          ]
        },
        {
          title: '政策支持',
          icon: 'ri-heart-add-line',
          content: [
            '【岗位开发】依据产业需求和毕业生专业特点开发见习岗位',
            '【供需对接】举办见习供需对接会，促进匹配',
            '【技能提升】鼓励基地提供技能培训提升',
            '【就业服务】提供职业指导和就业推荐',
            '【创业扶持】对有创业意愿的见习人员提供指导',
            '【宣传推广】加强政策宣传，提高知晓度和参与度'
          ]
        },
        {
          title: '注意事项',
          icon: 'ri-error-warning-line',
          content: [
            '见习期间不构成劳动合同关系，仅签订见习协议',
            '基地必须为见习人员投保人身意外伤害保险',
            '见习人员可参加公务员、事业单位等招聘考试，见习经历计入基层工作经历',
            '基地应按协议及时足额发放补贴',
            '违规使用补贴或提供虚假材料将被取消资格并追回资金'
          ]
        }
      ]
    }
  };

  // 如果选择了详细指南，显示指南内容
  if (selectedGuide && detailedGuides[selectedGuide as keyof typeof detailedGuides]) {
    const guide = detailedGuides[selectedGuide as keyof typeof detailedGuides];
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => setSelectedGuide(null)}
                  className="text-red-600 hover:text-red-800 cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-xl"></i>
                </button>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{guide.title}</h1>
                  <p className="text-lg text-gray-600 mt-2">政策补贴申请专业指导</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <button className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap">
                  收藏指南
                </button>
                <button className="border border-red-600 text-red-600 px-6 py-2 rounded-lg hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap">
                  打印指南
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Guide Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Navigation Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">指南目录</h3>
                <nav className="space-y-2">
                  {guide.sections.map((section, index) => (
                    <a
                      key={index}
                      href={`#section-${index}`}
                      className="flex items-center p-3 text-gray-700 hover:bg-red-50 hover:text-red-700 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className={`${section.icon} mr-3 text-red-600`}></i>
                      <span className="text-sm font-medium">{section.title}</span>
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="space-y-8">
                {guide.sections.map((section, index) => (
                  <div
                    key={index}
                    id={`section-${index}`}
                    className="bg-white rounded-xl shadow-lg overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-red-500 to-pink-600 px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4">
                          <i className={`${section.icon} text-white text-xl`}></i>
                        </div>
                        <h3 className="text-xl font-bold text-white">{section.title}</h3>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="space-y-4">
                        {section.content.map((item, itemIndex) => (
                          <div key={itemIndex} className="flex items-start">
                            <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <p className="text-gray-700 leading-relaxed">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="mt-8 bg-gradient-to-r from-red-500 to-pink-600 rounded-xl p-6 text-white">
                <div className="text-center">
                  <h3 className="text-2xl font-bold mb-4">需要专业协助？</h3>
                  <p className="text-red-100 mb-6">我们的专业顾问团队为您提供政策补贴申请的全程指导</p>
                  <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                    <button className="bg-white text-red-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                      在线咨询顾问
                    </button>
                    <button className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-red-600 transition-colors cursor-pointer whitespace-nowrap">
                      预约申请服务
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="text-red-600 hover:text-red-800 cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">政策补贴培训</h1>
                <p className="text-lg text-gray-600 mt-2">政府补贴政策咨询与申请指导</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-red-100 px-4 py-2 rounded-full">
                <span className="text-red-800 font-medium">学习进度: 0%</span>
              </div>
              <button className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap">
                开始学习
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/c09626ac5cf748c7b90fe42479e4363f.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-red-900/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            政府政策补贴服务
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-3xl mx-auto">
            为企业和个人提供全方位的政府补贴政策咨询与申请指导，助力享受政策红利
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="text-2xl font-bold mb-2">政策解读</div>
              <div className="text-red-100">专业分析</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="text-2xl font-bold mb-2">申请指导</div>
              <div className="text-red-100">全程协助</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="text-2xl font-bold mb-2">高效办理</div>
              <div className="text-red-100">快速申报</div>
            </div>
          </div>
        </div>
      </section>

      {/* Subsidy Types */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">主要补贴政策类型</h3>
            <p className="text-lg text-gray-600">覆盖创业就业、技能培训、社会保障等多个领域</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {subsidyTypes.map((subsidy) => (
              <div key={subsidy.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden group">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                      <i className={`${subsidy.icon} text-red-600 text-2xl`}></i>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900">{subsidy.title}</h4>
                      <p className="text-gray-600 text-sm">{subsidy.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-1">补贴金额</h5>
                      <p className="text-red-600 font-medium text-sm">{subsidy.amount}</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-1">发放方式</h5>
                      <p className="text-red-600 font-medium text-sm">{subsidy.period}</p>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h5 className="font-semibold text-gray-900 mb-2">申请条件</h5>
                    <div className="space-y-1">
                      {subsidy.conditions.map((condition, index) => (
                        <div key={index} className="flex items-center text-sm text-gray-600">
                          <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></div>
                          {condition}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <h5 className="font-semibold text-gray-900 mb-2">主要材料</h5>
                    <div className="flex flex-wrap gap-2">
                      {subsidy.materials.map((material, index) => (
                        <span 
                          key={index}
                          className="bg-red-50 text-red-700 px-2 py-1 rounded text-xs"
                        >
                          {material}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => setSelectedGuide(subsidy.id)}
                    className="w-full bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap group-hover:bg-red-700"
                  >
                    查看详细指南
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">补贴申请流程</h3>
            <p className="text-lg text-gray-600">标准化的申请流程，确保申请成功率</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
            {applicationProcess.map((process, index) => (
              <div key={process.step} className="text-center">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${process.icon} text-white text-2xl`}></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-red-800 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{process.step}</span>
                  </div>
                  {index < applicationProcess.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-red-200 -ml-10"></div>
                  )}
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{process.title}</h4>
                <p className="text-gray-600 mb-4 text-sm">{process.description}</p>
                <div className="space-y-1">
                  {process.details.map((detail, detailIndex) => (
                    <div key={detailIndex} className="text-xs text-gray-500">
                      • {detail}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Guide */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">申请平台指引</h3>
            <p className="text-lg text-gray-600">熟悉主要申请平台，提高办事效率</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {platformGuide.map((platform, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8">
                <div className="flex items-center mb-6">
                  <div className={`w-16 h-16 ${platform.color} rounded-full flex items-center justify-center mr-4`}>
                    <i className={`${platform.icon} text-white text-2xl`}></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">{platform.platform}</h4>
                    <p className="text-gray-600 text-sm">{platform.url}</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">{platform.description}</p>
                <div className="grid grid-cols-2 gap-2">
                  {platform.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center">
                      <i className="ri-check-line text-green-600 mr-2 text-sm"></i>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 rounded-xl p-6">
            <div className="flex items-center mb-4">
              <i className="ri-information-line text-blue-600 text-2xl mr-3"></i>
              <h4 className="text-xl font-bold text-gray-900">平台使用建议</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-700">
              <div>
                <h5 className="font-semibold mb-2">账号注册：</h5>
                <ul className="space-y-1 text-sm">
                  <li>• 使用真实身份信息注册</li>
                  <li>• 企业账号需法人实名认证</li>
                  <li>• 保存好账号密码信息</li>
                  <li>• 绑定手机号码便于接收通知</li>
                </ul>
              </div>
              <div>
                <h5 className="font-semibold mb-2">申请技巧：</h5>
                <ul className="space-y-1 text-sm">
                  <li>• 提前准备所有申请材料</li>
                  <li>• 仔细阅读申请须知</li>
                  <li>• 确保信息填写准确无误</li>
                  <li>• 及时关注审核进度</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Tips */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">申请注意事项</h3>
            <p className="text-lg text-gray-600">重要提醒，避免申请过程中的常见问题</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
                <div className="flex items-center mb-3">
                  <i className="ri-time-line text-amber-600 text-xl mr-2"></i>
                  <h4 className="text-lg font-semibold text-amber-800">申请时限</h4>
                </div>
                <p className="text-amber-700 text-sm">
                  各类补贴都有明确的申请时限，错过申请期将无法补办。建议企业建立政策申请台账，提前关注各项政策的申请时间节点。
                </p>
              </div>
              
              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <div className="flex items-center mb-3">
                  <i className="ri-shield-check-line text-red-600 text-xl mr-2"></i>
                  <h4 className="text-lg font-semibold text-red-800">诚信申请</h4>
                </div>
                <p className="text-red-700 text-sm">
                  严禁提供虚假材料或信息，一经发现将取消申请资格，已获得的补贴将被追回，并可能影响今后申请其他政策支持。
                </p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                <div className="flex items-center mb-3">
                  <i className="ri-file-text-line text-blue-600 text-xl mr-2"></i>
                  <h4 className="text-lg font-semibold text-blue-800">材料准备</h4>
                </div>
                <p className="text-blue-700 text-sm">
                  申请材料必须真实、完整、规范，所有复印件需加盖公章。建议提前核对材料清单，避免因材料不全影响申请进度。
                </p>
              </div>
              
              <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
                <div className="flex items-center mb-3">
                  <i className="ri-customer-service-line text-green-600 text-xl mr-2"></i>
                  <h4 className="text-lg font-semibold text-green-800">咨询服务</h4>
                </div>
                <p className="text-green-700 text-sm">
                  对政策理解有疑问时，及时咨询相关部门或专业机构。各级人社部门均设有政策咨询热线，可提供权威解答。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-pink-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">政策补贴的重要作用</h3>
            <p className="text-xl text-red-100">政府政策支持为企业和个人发展提供强有力保障</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">减轻负担</h4>
              <p className="text-red-100 text-sm">降低创业就业成本</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-rocket-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">激发活力</h4>
              <p className="text-red-100 text-sm">促进创新创业发展</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-team-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">促进就业</h4>
              <p className="text-red-100 text-sm">带动更多人员就业</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-line-chart-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">推动发展</h4>
              <p className="text-red-100 text-sm">助力经济高质量发展</p>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">政策效果数据</h3>
            <p className="text-lg text-gray-600">政策补贴实施效果显著</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="bg-gradient-to-br from-red-500 to-pink-600 rounded-xl p-6 text-white text-center">
              <div className="text-3xl font-bold mb-2">10万+</div>
              <div className="text-red-100">受益企业</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-6 text-white text-center">
              <div className="text-3xl font-bold mb-2">50万+</div>
              <div className="text-blue-100">受益人次</div>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6 text-white text-center">
              <div className="text-3xl font-bold mb-2">100亿+</div>
              <div className="text-green-100">补贴资金</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl p-6 text-white text-center">
              <div className="text-3xl font-bold mb-2">95%</div>
              <div className="text-purple-100">申请成功率</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-white mb-4">
            专业政策补贴咨询服务
          </h3>
          <p className="text-xl text-gray-300 mb-8">
            让专业的团队帮您识别适合的政策，提高申请成功率，及时享受政策红利
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button className="bg-red-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap">
              免费政策评估
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">
              咨询申请方案
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h4 className="text-lg font-semibold mb-4">联系我们</h4>
              <div className="space-y-2 text-gray-300">
                <p>电话: 020-1234-5678</p>
                <p>邮箱: policy@example.com</p>
                <p>地址: 广州市天河区xxx路123号</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">快速链接</h4>
              <div className="space-y-2 text-gray-300">
                <p className="cursor-pointer hover:text-white" onClick={() => window.REACT_APP_NAVIGATE('/')}>返回首页</p>
                <p>政策查询</p>
                <p>申请指导</p>
                <p>常见问题</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">热门补贴</h4>
              <div className="space-y-2 text-gray-300">
                <p>创业带动就业补贴</p>
                <p>灵活就业社保补贴</p>
                <p>一次性创业资助</p>
                <p>职业技能培训补贴</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">服务支持</h4>
              <div className="space-y-2 text-gray-300">
                <p>政策解读</p>
                <p>申请代办</p>
                <p>进度跟踪</p>
                <p><a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors">Made with Readdy</a></p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 企服培训平台. 保留所有权利.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
